import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { api } from '@/api/client';
import { isStaffFromPermissions } from '@/features/admin-content/staff';
import { authClient } from '@/lib/auth-client';

export type AppHomePath = '/admin' | '/dashboard';

/** After sign-in: staff go to Workspace, everyone else to the student dashboard. */
export async function homePathForSession(): Promise<AppHomePath> {
  try {
    const me = await api.get('/api/v1/me');
    if (isStaffFromPermissions(me.permissions)) return '/admin';
  } catch {
    // Missing /me should not block login — land on the student home.
  }
  return '/dashboard';
}

/** Auth pages only — send signed-in users to their app home. */
export async function redirectIfAuthenticated(): Promise<void> {
  const { data: session } = await authClient.getSession();
  if (session) {
    const to = await homePathForSession();
    // TanStack Router redirects are thrown objects by design, not Error instances.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to });
  }
}

/** True for the staff portal's URL space. */
export function isStaffPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

/**
 * Staff portal guard. Anonymous visitors go to sign-in; signed-in non-staff go back to the student
 * dashboard. The API enforces the same split with 403 on every staff endpoint; this keeps the
 * staff pages from rendering at all.
 */
export async function requireStaff(queryClient: QueryClient): Promise<void> {
  const { data: session } = await authClient.getSession();
  if (!session) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/sign-in' });
  }
  try {
    const me = await queryClient.query({
      queryKey: ['me'],
      queryFn: () => api.get('/api/v1/me'),
      staleTime: 30_000,
    });
    if (isStaffFromPermissions(me.permissions)) return;
  } catch {
    // Fall through: if we cannot prove staff, keep the user out.
  }
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({ to: '/dashboard' });
}
